import React, { useContext, useMemo, useState } from 'react';
import HeaderPreview from '../../../GeneratePdf/HeaderPreview';
import FooterPreview from '../../../GeneratePdf/FooterPreview';
import { initialData } from './QuadrantInFocus';
import GroupReportContext from '../../../../Context/GroupReportContext';

const GroupStrengthPreview = ({ apiData, previewData, reportType, pageNumber }) => {
  // console.log("GroupStrengthPreview apiData", apiData);
  // console.log("GroupStrengthPreview previewData", previewData);

  const groupReportCtx = useContext(GroupReportContext)
  const [dynamicData, setDynamicData] = useState([...initialData]);

  const questions = [
    {
      id: 1,
      question: "When presented with a problem, I immediately start working on it.",
      variableBase: "st1"
    },
    {
      id: 2,
      question: "I make decisions by focusing on what needs to get done.",
      variableBase: "st2"
    },
    {
      id: 3,
      question: "I feel most fulfilled when I get my tasks completed.",
      variableBase: "st3"
    },
    {
      id: 4,
      question: "I am motivated by winning.",
      variableBase: "st4"
    },
    {
      id: 5,
      question: "I rely heavily on my drive to complete a project.",
      variableBase: "st5"
    },
    {
      id: 6,
      question: "I respect people who deliver results.",
      variableBase: "st6"
    },
    {
      id: 7,
      question: "I gain the most satisfaction when I reach my goals.",
      variableBase: "st7"
    },
    {
      id: 8,
      question: "I can lose momentum without clear, measurable goals.",
      variableBase: "st8"
    },
    {
      id: 9,
      question: "I like turning good ideas into action.",
      variableBase: "st9"
    },
    {
      id: 10,
      question: "I take satisfaction in a hard day's work.",
      variableBase: "st10"
    }
  ];

  // Render gold squares based on average score (full/partial)
  const renderRectangle = (avgScore, index) => {
    const full = Math.floor(avgScore);
    const partial = avgScore - full;
    if (index < full) {
      // Full gold square
      return <div style={styles.rectangle}></div>;
    } else if (index === full && partial > 0) {
      // Partial gold square (only gold part, no light background)
      return (
        <div style={{ ...styles.rectangle, position: 'relative', overflow: 'hidden', backgroundColor: 'transparent' }}>
          <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: `${partial * 100}%`,
            height: '100%',
            backgroundColor: '#C99212',
            borderRadius: '2px',
            zIndex: 2,
          }}></div>
        </div>
      );
    } else {
      return null;
    }
  };

  const styles = {
    container: { paddingTop: '16px' },
    heading: { fontSize: "14px", paddingBottom: "16px", lineHeight: "18px", fontWeight: 700, color: "#426B88", margin: 0, fontFamily: "'Helvetica', sans-serif", letterSpacing: 0 },
    description: { marginBottom: "16px", color: "#000000", marginTop: 0, fontFamily: "'Helvetica', sans-serif", fontWeight: 400, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px' },
    tableWrapper: { overflow: "hidden", border: "0.25px solid #0000001A", borderRadius: "10px" },
    tableWrapper: { overflow: "hidden", border: "0.25px solid #0000001A", borderRadius: "10px" },
    table: { width: "100%", borderCollapse: "separate", borderSpacing: "0", lineHeight:"13px",  },
    th: { padding: "0 12px", backgroundColor: "#245775", color: "white", textAlign: "center", fontFamily: 'Helvetica', fontWeight: 400, lineHeight:"13px", fontStyle: 'normal', fontSize: '9px', letterSpacing: '0px' },
    td: { borderLeft: "0.65px solid #0000001A", padding: "5px", textAlign: "center", fontFamily: 'Helvetica', fontWeight: 400, fontStyle: 'normal', fontSize: '9px', letterSpacing: '0px', color: "#000000" },
    strengthColumn: { width: "100%", textAlign: "left", padding: "5.5px 15px", border: "none" },
    rowEven: { backgroundColor: "#FFF" },
    rowOdd: { backgroundColor: "#F1F3F3" },
    rectangleWrapper: { display: "flex", justifyContent: "center", alignItems: "center", height: "26px", width: "25px", margin: "0 auto" },
    rectangle: { width: "16px", height: "16px", backgroundColor: "#C99212", borderRadius: "1.34px" }
  };


  const displayData = dynamicData?.filter((el) => el.quadrant == "STRENGTH" );
  const allVariableSets = Array.isArray(apiData?.results) ? apiData.results.map((el) => el.variables) : [];

  const quadrantScores = {
    "HEART": 1,
    "SOUL": 2,
    "MIND": 3,
    "STRENGTH": 4
  }
  const allResults = [];
  displayData?.forEach((el, index) => {
    // console.log(el.quadrant, quadrantScores?.[el.quadrant])
    const res = groupReportCtx?.calculateQuestionScore(quadrantScores?.[el.quadrant], allVariableSets, index + 1, "in_focus");
    if (res?.averageScore !== null && quadrantScores?.[el.quadrant] == 4) {
      allResults.push(res);
    }
  })

  // console.log("allResults", allResults)

  return (
    <div>
      <HeaderPreview />
      <div style={styles.container}>
        <h1 style={styles.heading}>STRENGTH IN FOCUS</h1>
        <p style={styles.description}>
          This chart isolates the items related to the Strength based on the combined ratings of participants. The response scale runs from 1 (Least) to 4 (Most). Any unselected statement will appear blank in the In Focus section.
        </p>

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, ...styles.strengthColumn }}>Strength</th>
                <th style={{ ...styles.th, ...styles.strengthColumn }}>Score</th>
                {[1, 2, 3, 4].map((num) => (
                  <th key={num} style={styles.th}>{num}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {questions.map((item, index) => {
                const avgScore = allResults[index]?.averageScore;
                return (
                  <tr key={index} style={(index % 2) === 0 ? styles.rowOdd : styles.rowEven}>
                    <td style={{ ...styles.td, ...styles.strengthColumn }}>{item.question}</td>
                    <td style={{ ...styles.td }}>{parseFloat(avgScore) > 0 ? avgScore : "-"}</td>
                    {[0, 1, 2, 3].map((idx) => (
                      <td key={idx} style={styles.td}>
                        <div style={styles.rectangleWrapper}>
                          {renderRectangle(avgScore, idx)}
                        </div>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <FooterPreview pageNumber={pageNumber} reportType={reportType} />
    </div>
  );
};

export default GroupStrengthPreview;
